import mapValues from "lodash/mapValues"
import * as React from "react"
import { HTMLAttributes, useContext, useEffect, useReducer, useRef } from "react"

import { darkTheme, defaultTheme } from "../../theme"
import { Button } from "../button"
import * as navStyles from "./navbar.css"
import { LinkSpan } from "./typography.css"

type NavBarState = {
  mobileOpen: boolean
  navItems: { [key: string | number]: boolean }
}

type NavBarAction =
  | { type: "TOGGLE_NAV_ITEM"; navItemKey: string | number }
  | { type: "TOGGLE_MOBILE_MENU" }
  | { type: "CLOSE" }
function navBarReducer(state: NavBarState, action: NavBarAction): NavBarState {
  switch (action.type) {
    case "TOGGLE_MOBILE_MENU":
      return {
        ...state,
        mobileOpen: !state.mobileOpen,
        navItems: { ...mapValues(state.navItems, () => false) },
      }
    case "TOGGLE_NAV_ITEM":
      return {
        ...state,
        navItems: {
          ...mapValues(state.navItems, () => false),
          [action.navItemKey]: !state.navItems[action.navItemKey],
        },
      }
    case "CLOSE":
      return {
        ...state,
        navItems: mapValues(state.navItems, () => false),
        mobileOpen: false,
      }
    default:
      return state
  }
}
let lastId = 0
function _nextId() {
  lastId++
  return lastId
}
type NavBarContextType = {
  nextId: typeof _nextId
  state: NavBarState
  dispatch: React.Dispatch<NavBarAction>
  lightTheme: boolean
}
export const NavBarContext = React.createContext<NavBarContextType>(
  {} as NavBarContextType
)
type NavBarProps = { children: React.ReactNode; lightTheme: boolean, clearBg?: boolean }
export const NavBar = (props: NavBarProps) => {
  const themeToUse = props.lightTheme ? defaultTheme : darkTheme
  const [state, dispatch] = useReducer(navBarReducer, {
    navItems: {},
    mobileOpen: false,
  })

  useEffect(() => {
    const resize = () => {
      if (window.innerWidth >= themeToUse.header.mobileNavigationBreakpoint) {
        dispatch({ type: "CLOSE" })
      }
    }
    window.addEventListener("resize", resize)
    return () => {
      window.removeEventListener("resize", resize)
    }
  }, [])

  useEffect(() => {
    if (state.mobileOpen) {
      window.document.body.classList.add("mobile-nav-opened")
    } else {
      window.document.body.classList.remove("mobile-nav-opened")
    }
  }, [state.mobileOpen])

  return (
    <NavBarContext.Provider
      value={{ lightTheme: props.lightTheme, nextId: _nextId, state, dispatch }}
    >
      {props.children}
    </NavBarContext.Provider>
  )
}

export const NavBarInner = (props: any) => 
  <div {...props} className={navStyles.NavBarInnerStyle}>{props.children}</div>

type NavLinksProps = HTMLAttributes<HTMLDivElement> & {
  lightTheme: boolean
  color: ColorType
  className?: string
}

export const NavItemsContainer = ({lightTheme, color, className, ...rest}: NavLinksProps) =>
  <div {...rest} className={`${className} ${navStyles.NavItemsContainer}`}>
    {rest.children}
  </div>

type DropdownPositionedProps = {
  children: React.ReactNode
  className?: string
  lightTheme?: boolean
}
const DropdownPositioned = ({
  children,
  className,
}: DropdownPositionedProps) => (
  <div className={`${className} ${navStyles.panelOuter}`}>
    <div className={navStyles.panelInner}>
      <div className={navStyles.panelSpacer}>{children}</div>
    </div>
  </div>
)

const NavLink = (props: any) => <a {...props} className={`${navStyles.NavLink[props.customColor as "indigo" | "teal" ?? "indigo"]} ${LinkSpan}`}>{props.children}</a>

type ColorType = "indigo" | "teal" | "white" | undefined

type NavItemProps = {
  href?: string
  title: React.ReactNode
  dropdown?: React.ReactNode
  active?: boolean
  onClick?: () => void
  lightTheme: boolean
  color?: ColorType
}

export const NavItem = ({
  href,
  title,
  dropdown,
  active,
  onClick,
  color,
  lightTheme,
}: NavItemProps) => {
  const instanceRef = useRef({ id: 0 })
  const context = useContext(NavBarContext)
  useEffect(() => {
    instanceRef.current.id = context.nextId()
  }, [])

  const navlink = (href?: string) => (
    <NavLink
      onClick={
        !href
          ? () =>
              context.dispatch({
                type: "TOGGLE_NAV_ITEM",
                navItemKey: instanceRef.current.id,
              })
          : undefined
      }
      customColor={color}
      href={href ? href : undefined}
      className={dropdown ? navStyles.hasDropdown : ""}
    >
      {title}
      {dropdown && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          className="item-chevron"
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m6 9l6 6l6-6"
          />
        </svg>
      )}
    </NavLink>
  )

  return (
    <div
      className={`
        ${navStyles.NavItem} 
        ${context.state.navItems[instanceRef.current.id] ? navStyles.open : ``} 
        ${active ? navStyles.activeEl[color ? (color as "indigo" | "teal") : "indigo"] : ``}
      `}
      onClick={onClick}
    >
      {navlink(href)}
      {dropdown && <DropdownPositioned>{dropdown}</DropdownPositioned>}
    </div>
  )
}

export const MenuButton = ({ className, color }: { className?: string, color?: ("indigo" | "teal" | "white")}) => {
  const {
    state: { mobileOpen },
    dispatch,
  } = useContext(NavBarContext)
  return (
    <Button
      color={color ? color : "indigo"}
      onClick={() => dispatch({ type: "TOGGLE_MOBILE_MENU" })}
      className={`${className} menu-toggle-button `}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {mobileOpen ? (
          <>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </>
        ) : (
          <>
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </>
        )}
      </svg>
    </Button>
  )
}
